import { useState } from 'react';
import {
  Users,
  TrendingUp,
  Award,
  Star,
  UserPlus,
  Trophy,
  Activity
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts';

interface TeamManagementProps {
  topPerformers: any[];
  totalPlayers: number;
  activeCoaches: number;
}

export function TeamManagement({ topPerformers, totalPlayers, activeCoaches }: TeamManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState('all');
  const [selectedCoach, _setSelectedCoach] = useState('all');

  // Mock team data
  const teams = [
    {
      id: '1',
      name: 'Elite Squad',
      coach: 'Sarah Wilson',
      players: 12,
      avgImprovement: 28.5,
      skillLevel: 'Advanced',
      performance: 'Excellent',
      nextSession: '2024-08-19T09:00:00Z'
    },
    {
      id: '2',
      name: 'Development Group',
      coach: 'Mike Davis',
      players: 15,
      avgImprovement: 22.1,
      skillLevel: 'Intermediate',
      performance: 'Good',
      nextSession: '2024-08-19T14:00:00Z'
    },
    {
      id: '3',
      name: 'Youth Academy',
      coach: 'Lisa Brown',
      players: 18,
      avgImprovement: 35.2,
      skillLevel: 'Beginner',
      performance: 'Excellent',
      nextSession: '2024-08-19T16:00:00Z'
    },
    {
      id: '4',
      name: 'Senior Squad',
      coach: 'James Lee',
      players: 10,
      avgImprovement: 15.8,
      skillLevel: 'Advanced',
      performance: 'Good',
      nextSession: '2024-08-20T10:00:00Z'
    }
  ];

  // Mock player development pathway data
  const developmentPathways = [
    {
      level: 'Beginner',
      players: 45,
      avgDuration: '3-6 months',
      successRate: 92,
      nextLevel: 'Intermediate'
    },
    {
      level: 'Intermediate',
      players: 68,
      avgDuration: '6-12 months',
      successRate: 85,
      nextLevel: 'Advanced'
    },
    {
      level: 'Advanced',
      players: 32,
      avgDuration: '12+ months',
      successRate: 78,
      nextLevel: 'Elite'
    },
    {
      level: 'Elite',
      players: 11,
      avgDuration: 'Ongoing',
      successRate: 95,
      nextLevel: 'Professional'
    }
  ];

  // Mock skill distribution data
  const skillDistribution = [
    { name: 'Beginner', value: 45, color: '#3B82F6' },
    { name: 'Intermediate', value: 68, color: '#10B981' },
    { name: 'Advanced', value: 32, color: '#F59E0B' },
    { name: 'Elite', value: 11, color: '#8B5CF6' }
  ];

  // Mock performance benchmarking data
  const performanceBenchmarks = [
    { metric: 'Distance', beginner: 45, intermediate: 65, advanced: 85, elite: 95 },
    { metric: 'Accuracy', beginner: 40, intermediate: 60, advanced: 80, elite: 92 },
    { metric: 'Consistency', beginner: 35, intermediate: 55, advanced: 75, elite: 88 },
    { metric: 'Technique', beginner: 38, intermediate: 58, advanced: 78, elite: 90 }
  ];

  const getPerformanceBadge = (performance: string) => {
    switch (performance.toLowerCase()) {
      case 'excellent':
        return <Badge className="bg-green-500">Excellent</Badge>;
      case 'good':
        return <Badge className="bg-blue-500">Good</Badge>;
      case 'average':
        return <Badge variant="secondary">Average</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getSkillLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'elite':
        return 'text-purple-600';
      case 'advanced':
        return 'text-orange-600';
      case 'intermediate':
        return 'text-green-600';
      case 'beginner':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         team.coach.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSkill = selectedSkillLevel === 'all' || team.skillLevel.toLowerCase() === selectedSkillLevel;
    const matchesCoach = selectedCoach === 'all' || team.coach === selectedCoach;
    
    return matchesSearch && matchesSkill && matchesCoach;
  });

  return (
    <div className="space-y-6">
      {/* Team Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Players</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPlayers}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+8 new</span> this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Teams</CardTitle>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{teams.length}</div>
            <p className="text-xs text-muted-foreground">
              Across {activeCoaches} coaches
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Team Size</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(totalPlayers / teams.length)}
            </div>
            <p className="text-xs text-muted-foreground">
              Players per team
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Academy Ranking</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center">
              #3
              <Star className="h-5 w-5 text-yellow-500 ml-2" />
            </div>
            <p className="text-xs text-muted-foreground">
              Regional ranking
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Team Management Tabs */}
      <Tabs defaultValue="teams" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="teams">Team Overview</TabsTrigger>
          <TabsTrigger value="development">Development Pathways</TabsTrigger>
          <TabsTrigger value="performance">Performance Analysis</TabsTrigger>
          <TabsTrigger value="ranking">Academy Ranking</TabsTrigger>
        </TabsList>

        <TabsContent value="teams" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Team Management</CardTitle>
              <CardDescription>
                Manage teams, assign players, and track performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Input
                    placeholder="Search teams or coaches..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>
                <Select value={selectedSkillLevel} onValueChange={setSelectedSkillLevel}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Skill Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                  </SelectContent>
                </Select>
                <Button>
                  <UserPlus className="h-4 w-4 mr-2" />
                  New Team
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team Name</TableHead>
                    <TableHead>Coach</TableHead>
                    <TableHead>Players</TableHead>
                    <TableHead>Skill Level</TableHead>
                    <TableHead>Avg Improvement</TableHead>
                    <TableHead>Performance</TableHead>
                    <TableHead>Next Session</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTeams.map((team) => (
                    <TableRow key={team.id}>
                      <TableCell className="font-medium">{team.name}</TableCell>
                      <TableCell>{team.coach}</TableCell>
                      <TableCell>{team.players}</TableCell>
                      <TableCell>
                        <span className={getSkillLevelColor(team.skillLevel)}>
                          {team.skillLevel}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-green-600 font-semibold">
                          +{team.avgImprovement}%
                        </span>
                      </TableCell>
                      <TableCell>
                        {getPerformanceBadge(team.performance)}
                      </TableCell>
                      <TableCell>
                        {new Date(team.nextSession).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="development" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Player Development Pathways</CardTitle>
                <CardDescription>
                  Progress tracking across skill levels
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {developmentPathways.map((pathway, index) => (
                    <div key={pathway.level} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className={`font-semibold ${getSkillLevelColor(pathway.level)}`}>
                            {pathway.level}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {pathway.players} players • {pathway.avgDuration}
                          </p>
                        </div>
                        <Badge variant="outline">
                          {pathway.successRate}% success rate
                        </Badge>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progress to {pathway.nextLevel}</span>
                          <span>{pathway.successRate}%</span>
                        </div>
                        <Progress value={pathway.successRate} />
                      </div>
                      
                      {index < developmentPathways.length - 1 && (
                        <div className="text-center mt-3">
                          <TrendingUp className="h-4 w-4 text-muted-foreground mx-auto" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Skill Level Distribution</CardTitle>
                <CardDescription>
                  Current player distribution across skill levels
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={skillDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {skillDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Performance Benchmarks</CardTitle>
                <CardDescription>
                  Average performance metrics by skill level
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={performanceBenchmarks}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="metric" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="beginner" fill="#3B82F6" name="Beginner" />
                    <Bar dataKey="intermediate" fill="#10B981" name="Intermediate" />
                    <Bar dataKey="advanced" fill="#F59E0B" name="Advanced" />
                    <Bar dataKey="elite" fill="#8B5CF6" name="Elite" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Performers</CardTitle>
                <CardDescription>
                  Highest achieving players across all teams
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {topPerformers.slice(0, 8).map((performer, index) => (
                    <div key={performer.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                          #{index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{performer.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Coach: {performer.coachName}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center">
                          <Star className="h-3 w-3 text-yellow-500 mr-1" />
                          <span className="text-sm font-medium">{performer.score}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          +{performer.improvement}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="ranking" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Academy Rankings</CardTitle>
                <CardDescription>
                  Regional and national standing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary mb-2">#3</div>
                    <p className="text-lg font-semibold">Regional Ranking</p>
                    <p className="text-sm text-muted-foreground">Out of 25 academies</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-600">#12</div>
                      <p className="text-sm">National Ranking</p>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">4.6★</div>
                      <p className="text-sm">Academy Rating</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Ranking Factors</CardTitle>
                <CardDescription>
                  Key metrics contributing to academy ranking
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Player Improvement</span>
                      <span className="text-green-600 font-semibold">92/100</span>
                    </div>
                    <Progress value={92} />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Coach Quality</span>
                      <span className="text-blue-600 font-semibold">88/100</span>
                    </div>
                    <Progress value={88} />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Facilities</span>
                      <span className="text-yellow-600 font-semibold">75/100</span>
                    </div>
                    <Progress value={75} />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Student Satisfaction</span>
                      <span className="text-green-600 font-semibold">94/100</span>
                    </div>
                    <Progress value={94} />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Competition Performance</span>
                      <span className="text-purple-600 font-semibold">81/100</span>
                    </div>
                    <Progress value={81} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}